import Immutable from "immutable";

import ScenarioAction, {
  notForSimpleViewActions,
  scenarioActionTypes,
} from "./ScenarioAction";
import _sum from "lodash/sum";
import { moduleIds } from "./Module";
import { Colors } from "../style/colors";
import { presets } from "../../components/WeekAvailability";
import dayjs, { Dayjs } from "dayjs";
import { sundayFirstToMondayFirst } from "../functions";
import validator from "validator";
import { getWhitelabel } from "../../env";
import { scenarioTriggers } from "./scenarios/scenarioTriggers";
import { triggerConfigs } from "./scenarios/triggerConfigs";
import { scenarioTriggersMap } from "./scenarios/triggerConfigMap";
import { triggerLabels } from "./scenarios/triggerLabels";
import ScenarioLog from "./ScenarioLog";

export const forAnyObject = -1;
export const forConcerningObject = -2;

export const triggerCategories = Object.fromEntries(
  Object.keys(scenarioTriggers).map((trigger) => {
    return [
      trigger,
      Object.keys(scenarioTriggersMap).find(
        (category) =>
          scenarioTriggersMap[category].triggers.indexOf(trigger) >= 0
      ),
    ];
  })
);

export const accountManagerTriggers = [
  scenarioTriggers.configError,
  scenarioTriggers.beaconBattery,
  scenarioTriggers.connectionChanged,
  scenarioTriggers.allAlarmTypes,
  scenarioTriggers.genericAlarm,
  scenarioTriggers.lowPrioAlarm,
  scenarioTriggers.sosButton,
  scenarioTriggers.manDown,
  scenarioTriggers.ripcord,
  scenarioTriggers.timer,
  scenarioTriggers.arcAlarm,
];

export const notForSimpleView = [
  scenarioTriggers.checkAssistanceRequestResponses,
  scenarioTriggers.multiTenant,
  scenarioTriggers.email,
];
export const devicelessTriggers = [
  scenarioTriggers.webhook,
  scenarioTriggers.quickButton,
  scenarioTriggers.email,
  scenarioTriggers.multiTenant,
  scenarioTriggers.checkAssistanceRequestResponses, //not actually deviceless, but there is no compatibility entry for this trigger
  scenarioTriggers.scenarioDisabled,
];
export const geofenceTriggers = [
  scenarioTriggers.geofenceExited,
  scenarioTriggers.geofenceEntered,
  scenarioTriggers.geofenceAttendance,
];
export const teamTriggers = [scenarioTriggers.teamAttendance];
export const beaconTrigger = [scenarioTriggers.beaconBattery];
export const adminScenarioTriggers = [scenarioTriggers.email];

export const colorForTrigger = (trigger) => {
  const category = Object.values(scenarioTriggersMap).find(
    (t) => t.triggers.indexOf(trigger) >= 0
  );
  return category ? category.color : Colors.gray30;
};
export const alertTriggers = [
  scenarioTriggers.connectionChanged,
  scenarioTriggers.docked,
  scenarioTriggers.undocked,
  scenarioTriggers.lowBattery,
  scenarioTriggers.quickButton,
  scenarioTriggers.ioChange,
  scenarioTriggers.geofenceEntered,
  scenarioTriggers.geofenceExited,
  scenarioTriggers.geofenceAttendance,
  scenarioTriggers.multiTenant,
  scenarioTriggers.espa,
  scenarioTriggers.noAlarmTriggered,
];

const ScenarioModel = Immutable.Record({
  id: 0,
  name: "",
  trigger: "",
  triggerConfig: [],
  accounts: null,
  actions: [ScenarioAction.create("email")],
  logs: [ScenarioLog.create()],
  enabled: true,
  locked: false,
  visible: true,
  schedule: [],
  source: "v3",
  v4Data: null,
});

export default class Scenario extends ScenarioModel {
  id: number;
  name: string;
  trigger: string;
  triggerConfig: any;
  accounts: number[];
  actions: ScenarioAction[];
  logs: ScenarioLog[];
  enabled: boolean;
  locked: boolean;
  visible: boolean;
  schedule: boolean[][];

  static compatibility: any = {};

  canSave(devices, teams, geofences): boolean {
    // V4 requires name >= 3 chars + >=1 trigger with a fully-configured
    // source. canSaveActions covers the actions-array requirement.
    if (this.isV4) {
      const name = this.get("name") || "";
      if (name.length < 3) return false;
      if (!this.get("trigger")) return false;
      return this.triggerConfigured(devices, teams, geofences);
    }
    return !!(
      this.get("name") &&
      this.get("trigger") &&
      this.triggerConfigured(devices, teams, geofences)
    );
  }

  canEdit(user): boolean {
    // V4 scenarios route through saveV4ScenarioSaga — bypass the V3 `locked` flag.
    if (this.isV4) return user.isShadow || !user.isPortalUser;
    if (user.isShadow) return true;
    return !this.get("locked") && !user.isPortalUser;
  }

  get isV4(): boolean {
    return this.get("source") === "v4";
  }

  getLastRun(): null | ScenarioLog {
    if (!this.logs) return null;
    return this.logs[0];
  }

  get hasGeneratedConfig() {
    return (
      [scenarioTriggers.webhook, scenarioTriggers.email].indexOf(
        this.get("trigger")
      ) >= 0
    );
  }

  containsDevices(devices, user) {
    const deviceIds = devices.map((d) => d.id);
    if (this.get("actions").find((a) => a.containsDevices(devices, user, this)))
      return true;
    if (this.get("triggerConfig")?.deviceIds?.indexOf(forAnyObject) >= 0) {
      return !!devices.find((d) => d.isCompatibleWithTrigger(this.trigger));
    }
    return (
      deviceIds.indexOf(this.get("triggerConfig")?.deviceId) >= 0 ||
      this.get("triggerConfig")?.deviceIds?.find(
        (dId) => deviceIds.indexOf(dId) >= 0
      )
    );
  }
  containsTeamIds(teamIds) {
    if (this.get("actions").find((a) => a.containsTeamIds(teamIds)))
      return true;
    return teamIds.indexOf(this.get("triggerConfig")?.teamId) >= 0;
  }
  containsGeofenceIds(geofenceIds) {
    if (this.get("actions").find((a) => a.containsGeofenceIds(geofenceIds)))
      return true;
    return geofenceIds.indexOf(this.get("triggerConfig")?.geofenceId) >= 0;
  }

  createCopy() {
    const copy = this.set("id", 0).set("name", `${this.get("name")} [2]`);
    if (this.hasGeneratedConfig) {
      return copy.set("triggerConfig", null);
    }
    return copy;
  }

  get canSaveActions() {
    // V4 requires >=1 action with a non-empty type. V3 per-type config
    // validators (recipients/text/whenEmpty …) don't apply — V4 validates
    // its own action.config server-side.
    if (this.isV4) {
      const actions = this.get("actions") || [];
      if (!actions.length) return false;
      return !actions.find((a) => !a.get("type"));
    }
    return !this.get("actions").find((a) => !a.canSave);
  }

  triggerConfigured(devices, teams, geofences) {
    const triggerConfigType = triggerConfigs[this.get("trigger")];
    const config = this.get("triggerConfig");

    if (["beacons", "geofences"].indexOf(triggerConfigType) >= 0) {
      return Array.isArray(config) && config.length > 0;
    } else if (
      ["devices", "connectionChanged"].indexOf(triggerConfigType) >= 0
    ) {
      if (triggerConfigType === "connectionChanged") {
        if (!(+config?.reasons?.length > 0)) return false;
      }
      if (!config?.source) return false;
      if (config.source === "devices") {
        if (!Array.isArray(config.deviceIds)) return false;

        return (
          config.deviceIds.filter(
            (dId) => dId < 0 || devices.find((d) => d.id === dId)
          ).length > 0
        );
      }
      if (config.source === "teams")
        return !!config.teamIds.map((tId) => teams.find((t) => t.id === tId))
          .length;
      if (config.source === "geofences")
        return !!config.geofenceIds.map((gId) =>
          geofences.find((g) => g.id === gId)
        ).length;
      return false;
    } else if (triggerConfigType === "multiTenant") {
      return config?.code?.length === 20;
    } else if (triggerConfigType === "email") {
      if (config instanceof Array) return false;
      return !config?.fromFilter || validator.isEmail(config?.fromFilter);
    } else if (triggerConfigType === "io") {
      return !!config?.deviceIds?.length && !!config?.ioTrigger;
    }
    return true;
  }

  get triggerName(): string {
    return triggerLabels[this.get("trigger")] || "";
  }

  placeholders(user): [] {
    const placeholders = {
      date: "dateOfEvent",
      time: "timeOfEvent",
    };
    const t = scenarioTriggers;
    if (user.isAccountManager) {
      placeholders.account = "accountName";
    }
    if (t.webhook === this.get("trigger")) {
      placeholders.requestBody = "requestBody";
    }
    if (t.smsMessage === this.get("trigger")) {
      placeholders.smsText = "smsText";
    }
    if (
      [
        t.multiTenant,
        t.messageNotAcknowledged,
        t.checkAssistanceRequestResponses,
        t.assistanceRequestClosed,
      ].indexOf(this.get("trigger")) >= 0
    ) {
      placeholders.message = "message";
    }
    if (t.espa === this.get("trigger")) {
      placeholders.id = {
        tooltipRaw: "ID",
        label: "ID",
        key: "id",
      };
      placeholders.address = {
        tooltipRaw: "Address",
        label: "Address",
        key: "address",
      };
      placeholders.info = {
        tooltipRaw: "Info",
        label: "Info",
        key: "info",
      };
      placeholders.priority = {
        tooltipRaw: "Priority",
        label: "Priority",
        key: "priority",
      };
      placeholders.status = {
        tooltipRaw: "Status",
        label: "Status",
        key: "status",
      };
      placeholders.bleep = {
        tooltipRaw: "Bleep",
        label: "Bleep",
        key: "bleep",
      };
      placeholders.repeat = {
        tooltipRaw: "Repeat",
        label: "Repeat",
        key: "repeat",
      };
      placeholders.callType = {
        tooltipRaw: "Call type",
        label: "Call type",
        key: "callType",
      };
    }
    if (t.email === this.get("trigger")) {
      placeholders.contents = "contents";
      placeholders.subject = "subject";
      placeholders["from.email"] = {
        tooltip: "from.email",
        key: "from",
      };
    }
    if ([t.assistanceRequestClosed].indexOf(this.get("trigger")) >= 0) {
      placeholders.name = "name";
    }
    if ([t.alarmClosed].indexOf(this.get("trigger")) >= 0) {
      placeholders.alarmNotes = "alarmNotes";
    }
    if ([t.scenarioDisabled].indexOf(this.get("trigger")) >= 0) {
      placeholders.name = "name";
      placeholders.reason = "reason";
    }
    if (
      [
        t.lowBattery,
        t.noAlarmTriggered,
        t.smsMessage,
        t.genericAlarm,
        t.ioChange,
      ].indexOf(this.get("trigger")) >= 0
    ) {
      placeholders.device = "deviceName";
      placeholders.phone = "devicePhone";
    }
    if (beaconTrigger.indexOf(this.get("trigger")) >= 0) {
      placeholders.beacon = "beaconName";
    }
    if (
      [
        t.allAlarmTypes,
        t.lowPrioAlarm,
        t.manDown,
        t.timer,
        t.ripcord,
        t.sosButton,
        t.arcAlarm,
      ].indexOf(this.get("trigger")) >= 0
    ) {
      placeholders.alarmType = "alarmType";
      placeholders.alarmLocationUrl = "alarmLocationUrl.explanation";
    }
    if (
      [t.geofenceEntered, t.geofenceExited, t.geofenceAttendance].indexOf(
        this.get("trigger")
      ) >= 0
    ) {
      placeholders.device = "deviceName";
      placeholders.phone = "devicePhone";
      placeholders.geofence = "geofenceName";
      placeholders.locationUrl = "locationUrl.explanation";
    }
    if (
      [
        t.configError,
        t.lowBattery,
        t.allAlarmTypes,
        t.lowPrioAlarm,
        t.arcAlarm,
        t.manDown,
        t.timer,
        t.ripcord,
        t.sosButton,
        t.connectionChanged,
        t.quickButton,
        t.docked,
        t.undocked,
        t.idle,
        t.noAlarmTriggered,
      ].indexOf(this.get("trigger")) >= 0
    ) {
      placeholders.device = "deviceName";
      placeholders.phone = "devicePhone";
      placeholders.operatingAddress = "operatingAddress";
      placeholders.tempAddress = "tempAddress";
      placeholders.industry = "industry";
    }
    if (
      [
        t.allAlarmTypes,
        t.lowPrioAlarm,
        t.manDown,
        t.timer,
        t.ripcord,
        t.sosButton,
        t.connectionChanged,
        t.quickButton,
        t.docked,
        t.undocked,
      ].indexOf(this.get("trigger")) >= 0
    ) {
      placeholders.site = "site";
      placeholders.beacons = "beaconName.all";
      placeholders.beacon = "beaconName.strongest";
      placeholders.geofences = "allPresentGeofences";
      placeholders.geofence_sm = "smallestPresentGeofence";
      placeholders.location = "locationPlaceholder.explanation";
      placeholders.gmapsUrl = "gmapsUrl.explanation";
      placeholders.locationUrl = "locationUrl.explanation";
    }
    return placeholders;
  }

  creditCosts(devices, teams, geofences, beacons, alarmTypeNames) {
    const costs = this.get("actions").map((a) =>
      a.creditCosts(devices, teams, geofences, beacons, alarmTypeNames)
    );
    return {
      min: _sum(costs.map((c) => c.min)),
      max: _sum(costs.map((c) => c.max)),
    };
  }

  get isAlarmTrigger() {
    const t = scenarioTriggers;
    return (
      [
        t.allAlarmTypes,
        t.genericAlarm,
        t.manDown,
        t.timer,
        t.ripcord,
        t.sosButton,
        t.arcAlarm,
      ].indexOf(this.get("trigger")) >= 0
    );
  }

  canPerformAction(actionType, devices, user, adminMode) {
    if (
      getWhitelabel().simpleView === true &&
      notForSimpleViewActions.indexOf(actionType) >= 0
    )
      return false;
    if (user.isAccountManager) {
      return (
        [
          scenarioActionTypes.email,
          scenarioActionTypes.sms,
          scenarioActionTypes.sendTelegramMessage,
        ].indexOf(actionType) >= 0
      );
    }
    if (
      [
        scenarioTriggers.messageNotAcknowledged,
        scenarioTriggers.checkAssistanceRequestResponses,
      ].indexOf(this.trigger) >= 0
    ) {
      const specificTrigger =
        scenarioTriggers.messageNotAcknowledged === this.trigger
          ? scenarioActionTypes.resendLastOSM
          : scenarioActionTypes.assistanceRequest;
      return (
        [
          specificTrigger,
          scenarioActionTypes.osm,
          scenarioActionTypes.sms,
          scenarioActionTypes.voiceMessage,
          scenarioActionTypes.sendTelegramMessage,
        ].indexOf(actionType) >= 0
      );
    }
    const t = scenarioTriggers;
    if (actionType === scenarioActionTypes.safeguardAlarm) {
      return user.hasModule(moduleIds.Safeguard);
    }
    if (actionType === scenarioActionTypes.sendToARC) {
      const allowed = this.isAlarmTrigger || this.get("trigger") === t.connectionChanged;
      // `hasAlarmCenter` is the V3 `arcKey`, which bootstrapV4Native sets only
      // for an ARC-console session — false for a V4 customer that has an ARC.
      // No customer-readable V4 endpoint exposes the link, so V4 owns the check.
      return (
        allowed &&
        (this.isV4 || user.hasAlarmCenter) &&
        !this.actions.find((a) => a.type === scenarioActionTypes.sendToARC)
      );
    }
    if (actionType === scenarioActionTypes.showAsAlert) {
      return (
        alertTriggers.indexOf(this.get("trigger")) >= 0 ||
        this.get("trigger") === scenarioTriggers.webhook
      );
    }
    if (actionType === scenarioActionTypes.createAlarm) {
      return (
        [
          scenarioTriggers.smsMessage,
          scenarioTriggers.ioChange,
          scenarioTriggers.email,
        ].indexOf(this.get("trigger")) >= 0
      );
    }
    if (actionType === scenarioActionTypes.lowPrioSIA) {
      if (!user.hasModule(moduleIds.LowPrioSIA)) return false;
    }
    if (actionType === scenarioActionTypes.startTracking) {
      if (!user.hasModule(moduleIds.TrackingScenario)) return false;
      if (
        this.get("actions").find(
          (a) => a.get("type") === scenarioActionTypes.startTracking
        )
      )
        return false;
    }
    if (actionType === scenarioActionTypes.safeguardAlarm) {
      return this.isAlarmTrigger;
    }
    if (actionType === scenarioActionTypes.geofencePresence) {
      return (
        [t.docked, t.undocked, t.connectionChanged, t.quickButton].indexOf(
          this.get("trigger")
        ) >= 0
      );
    }
    if (actionType === scenarioActionTypes.changeTeam) {
      if (!user.hasModule(moduleIds.Teams)) return false;
      return (
        this.isAlarmTrigger ||
        [
          t.docked,
          t.undocked,
          t.configError,
          t.lowBattery,
          t.connectionChanged,
          t.quickButton,
          t.noAlarmTriggered,
        ].indexOf(this.get("trigger")) >= 0
      );
    }
    if (actionType === scenarioActionTypes.webhook) {
      return this.get("trigger") !== t.webhook;
    }
    if (actionType === scenarioActionTypes.multiTenant) {
      return this.get("trigger") !== t.multiTenant;
    }
    if (actionType === scenarioActionTypes.setDigitalOutput) {
      return devices?.find((d) => d.can("setDigitalOutput"));
    }
    if (
      [
        scenarioActionTypes.sendTelegramMessage,
        scenarioActionTypes.requestAppLogs,
      ].indexOf(actionType) >= 0
    ) {
      return adminMode;
    }
    return true;
  }

  addAction(type, initConfig, language = "nl-NL") {
    const actions = Array.from(this.get("actions"));
    actions.push(
      ScenarioAction.create(
        type,
        {
          waitForNewLocation: false,
          ...initConfig,
        },
        language
      )
    );
    return this.set("actions", actions);
  }

  get scheduleArray() {
    return this.get("schedule")?.length ? this.get("schedule") : presets.always;
  }

  get isDeviceTrigger() {
    return [scenarioTriggers.webhook].indexOf(this.get("trigger")) === -1;
  }

  setTrigger(trigger) {
    if (triggerConfigs[trigger] === triggerConfigs[this.get("trigger")]) {
      return this.set("trigger", trigger);
    }
    return this.set("actions", [])
      .set("triggerConfig", [])
      .set("trigger", trigger);
  }

  isAvailable(date: Dayjs) {
    if (!date) date = dayjs();
    const slot = date.hour() * 2 + (date.minute() > 30 ? 1 : 0);
    return this.isAvailableAtSlot(sundayFirstToMondayFirst(date.day()), slot);
  }

  isAvailableAtSlot(day: number, slot: number) {
    const dayAvailability = this.scheduleArray[day];
    return !!dayAvailability[slot];
  }

  constructor(props) {
    props.actions = (props.actions || []).map((a) => new ScenarioAction(a));
    props.logs = props.logs?.map((a) => new ScenarioLog(a)) ?? [];
    super(props);
  }

  clone() {
    return this.set("id", this.get("id")).set(
      "actions",
      this.get("actions").map((a) => a.clone())
    );
  }

  static create() {
    return new Scenario({
      id: 0,
      name: "",
      trigger: "",
      enabled: true,
      visible: true,
      locked: false,
      logs: [],
      actions: [],
    });
  }

  // `source: 'v4'` routes save through saveV4ScenarioSaga; `v4Data` gets
  // populated from the backend response.
  static createV4() {
    return new Scenario({
      id: 0,
      name: "",
      trigger: "",
      enabled: true,
      visible: true,
      locked: false,
      logs: [],
      actions: [],
      source: "v4",
      v4Data: {},
    });
  }
}
